// ============================================================
// BELLS OF STEEL — GEO-TARGETED PICKUP POPUP  v4.0
// ============================================================
// Custom HTMLElement: <geo-pickup-popup>
//
// Content is managed via the "Geo Pickup Popup" section in
// the Shopify theme editor (sections/geo-pickup-popup.liquid).
//
// Features:
//   - Geo detection (GeoJS primary, ipapi.co fallback)
//   - City/region/domain matching per showroom
//   - Dismiss cookie (configurable duration)
//   - Scroll % and time-delay triggers
//   - GA4 event tracking
//   - Shopify design-mode support
//
// Domains:
//   bellsofsteel.com  → Calgary, Toronto
//   bellsofsteel.us   → Indianapolis
//   bellsofsteel.au   → Melbourne
// ============================================================

if (!customElements.get('geo-pickup-popup')) {
  class GeoPickupPopup extends HTMLElement {

    // ==============================
    // SHOWROOM GEO-MATCHING DATA
    // ==============================
    static SHOWROOMS = {
      calgary: {
        label: 'Calgary',
        pageSlug: 'calgary-fitness-equipment',
        domains: [
          'bellsofsteel.com', 'www.bellsofsteel.com',
          'bells-of-steel.com', 'www.bells-of-steel.com'
        ],
        cities: [
          'calgary', 'airdrie', 'cochrane', 'okotoks', 'chestermere',
          'strathmore', 'high river', 'canmore', 'banff', 'red deer',
          'olds', 'didsbury', 'innisfail', 'sylvan lake', 'lacombe',
          'ponoka', 'drumheller', 'crossfield', 'carstairs', 'sundre',
          'nanton', 'vulcan', 'turner valley', 'black diamond',
          'lethbridge', 'medicine hat', 'brooks'
        ],
        regions: ['alberta']
      },
      toronto: {
        label: 'Toronto',
        pageSlug: 'toronto-fitness-equipment-store',
        domains: [
          'bellsofsteel.com', 'www.bellsofsteel.com',
          'bells-of-steel.com', 'www.bells-of-steel.com'
        ],
        cities: [
          'toronto', 'mississauga', 'brampton', 'markham', 'vaughan',
          'richmond hill', 'oakville', 'burlington', 'hamilton',
          'oshawa', 'whitby', 'ajax', 'pickering', 'scarborough',
          'north york', 'etobicoke', 'newmarket', 'aurora',
          'stouffville', 'king city', 'milton', 'georgetown',
          'caledon', 'bolton', 'orangeville', 'barrie', 'innisfil',
          'keswick', 'bradford', 'guelph', 'kitchener', 'waterloo',
          'cambridge', 'brantford', 'st. catharines', 'niagara falls',
          'welland', 'grimsby', 'stoney creek', 'ancaster',
          'dundas', 'woodbridge', 'thornhill', 'concord',
          'maple', 'nobleton', 'kleinburg', 'east york',
          'york', 'willowdale', 'don mills'
        ],
        regions: ['ontario']
      },
      indianapolis: {
        label: 'Indianapolis',
        pageSlug: 'indianapolis-fitness-equipment-store',
        domains: [
          'bellsofsteel.us', 'www.bellsofsteel.us',
          'bells-of-steel.us', 'www.bells-of-steel.us'
        ],
        cities: [
          'indianapolis', 'carmel', 'fishers', 'noblesville',
          'greenwood', 'plainfield', 'avon', 'brownsburg',
          'zionsville', 'westfield', 'lebanon', 'franklin',
          'shelbyville', 'greenfield', 'anderson', 'muncie',
          'bloomington', 'columbus', 'martinsville', 'mooresville',
          'danville', 'speedway', 'lawrence', 'beech grove',
          'southport', 'whiteland', 'trafalgar', 'bargersville',
          'cicero', 'arcadia', 'pendleton', 'fortville',
          'mccordsville', 'new palestine', 'whitestown',
          'pittsboro', 'terre haute', 'kokomo', 'lafayette'
        ],
        regions: ['indiana']
      },
      melbourne: {
        label: 'Melbourne',
        pageSlug: 'melbourne-fitness-equipment-showroom',
        domains: [
          'bellsofsteel.au', 'www.bellsofsteel.au',
          'bells-of-steel.au', 'www.bells-of-steel.au'
        ],
        cities: [
          'melbourne', 'south melbourne', 'north melbourne',
          'east melbourne', 'west melbourne', 'port melbourne',
          'st kilda', 'richmond', 'collingwood', 'fitzroy',
          'carlton', 'brunswick', 'footscray', 'yarraville',
          'williamstown', 'altona', 'werribee', 'hoppers crossing',
          'point cook', 'tarneit', 'truganina', 'caroline springs',
          'melton', 'sunbury', 'craigieburn', 'epping',
          'south morang', 'mill park', 'bundoora', 'preston',
          'coburg', 'essendon', 'moonee ponds', 'ascot vale',
          'maribyrnong', 'doncaster', 'templestowe', 'eltham',
          'greensborough', 'heidelberg', 'ivanhoe', 'kew',
          'hawthorn', 'camberwell', 'box hill', 'glen waverley',
          'mount waverley', 'clayton', 'dandenong', 'cranbourne',
          'frankston', 'mornington', 'pakenham', 'berwick',
          'narre warren', 'officer', 'clyde', 'geelong',
          'ballarat', 'bendigo', 'brighton', 'caulfield',
          'malvern', 'toorak', 'prahran', 'south yarra'
        ],
        regions: ['victoria']
      }
    };

    static GEO_API_URL = 'https://get.geojs.io/v1/ip/geo.json';
    static GEO_API_FALLBACK_URL = 'https://ipapi.co/json/';
    static COOKIE_NAME = 'bos_geo_banner';
    static GEO_COOKIE_NAME = 'bos_geo_city';
    static EXCLUDED_PATHS = [
      '/checkout', '/cart', '/account', '/account/login',
      '/account/register', '/account/orders',
      '/tools/recurring', '/challenge'
    ];

    constructor() {
      super();

      this.cookieDays = parseInt(this.dataset.cookieDays) || 90;
      this.scrollThreshold = parseInt(this.dataset.scrollThreshold) || 55;
      this.timeThreshold = (parseInt(this.dataset.timeDelay) || 60) * 1000;
      this.path = window.location.pathname.toLowerCase();

      this._scrollHandler = null;
      this._timeTimer = null;
    }

    connectedCallback() {
      // Shopify theme editor support
      if (Shopify.designMode) {
        this._bindDesignMode();
        return;
      }

      // Already dismissed?
      if (GeoPickupPopup.getCookie(GeoPickupPopup.COOKIE_NAME) === 'dismissed') return;

      // On an excluded page?
      for (const excluded of GeoPickupPopup.EXCLUDED_PATHS) {
        if (this.path.indexOf(excluded) === 0) return;
      }

      // Bind delegated click + keyboard listeners
      this._bindEvents();

      // Detect geo and queue popup
      this._detectGeo();
    }

    disconnectedCallback() {
      this._cleanupTriggers();
    }

    // ==============================
    // EVENT HANDLING (delegated)
    // ==============================
    _bindEvents() {
      this.addEventListener('click', (e) => {
        // Dismiss: backdrop, X button, "No thanks" link
        if (e.target.closest('.js-bos-geo-dismiss')) {
          const popup = e.target.closest('.bos-geo-popup');
          if (popup) this.dismiss(popup);
          return;
        }
        // CTA click tracking
        if (e.target.closest('.bos-geo-popup__cta')) {
          const popup = e.target.closest('.bos-geo-popup');
          if (popup) {
            const key = popup.dataset.showroom;
            const sr = GeoPickupPopup.SHOWROOMS[key];
            if (sr) GeoPickupPopup.trackEvent('banner_click', sr.label);
          }
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          const visible = this.querySelector('.bos-geo-popup:not([hidden])');
          if (visible) this.dismiss(visible);
        }
      });
    }

    // ==============================
    // DESIGN MODE (theme editor)
    // ==============================
    _bindDesignMode() {
      // Bind dismiss so the X/backdrop work in the editor too
      this._bindEvents();

      document.addEventListener('shopify:section:select', (e) => {
        if (e.target === this.closest('.shopify-section')) {
          const first = this.querySelector('.bos-geo-popup');
          if (first) this.open(first);
        }
      });

      document.addEventListener('shopify:section:deselect', () => {
        const visible = this.querySelector('.bos-geo-popup:not([hidden])');
        if (visible) this.dismiss(visible);
      });

      document.addEventListener('shopify:block:select', (e) => {
        const popup = e.target.closest('.bos-geo-popup');
        if (popup && this.contains(popup)) this.open(popup);
      });

      document.addEventListener('shopify:block:deselect', (e) => {
        const popup = e.target.closest('.bos-geo-popup');
        if (popup && this.contains(popup)) this.dismiss(popup);
      });
    }

    // ==============================
    // SHOW / DISMISS
    // ==============================
    open(popup) {
      // Close any currently visible popup first
      const visible = this.querySelector('.bos-geo-popup:not([hidden])');
      if (visible && visible !== popup) {
        visible.setAttribute('hidden', '');
        visible.classList.remove('bos-geo-popup--animate', 'bos-geo-popup--dismissing');
      }

      popup.removeAttribute('hidden');
      popup.classList.add('bos-geo-popup--animate');
      document.body.style.overflow = 'hidden';

      // WCAG 2.1.2 / 2.4.3: it's a modal dialog, so move focus into it, keep Tab inside it,
      // and give focus back to where it was when it closes.
      if (!Shopify.designMode) {
        this._returnFocus = document.activeElement;
        const dialog = popup.querySelector('[role="dialog"]') || popup;
        if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
        const first = dialog.querySelector('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
        (first || dialog).focus({ preventScroll: true });
        this._trap = (e) => {
          if (e.key !== 'Tab') return;
          const items = [...dialog.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null);
          if (!items.length) { e.preventDefault(); return; }
          const firstItem = items[0], lastItem = items[items.length - 1];
          if (!dialog.contains(document.activeElement)) { e.preventDefault(); firstItem.focus(); }
          else if (e.shiftKey && document.activeElement === firstItem) { e.preventDefault(); lastItem.focus(); }
          else if (!e.shiftKey && document.activeElement === lastItem) { e.preventDefault(); firstItem.focus(); }
        };
        document.addEventListener('keydown', this._trap, true);
      }

      const key = popup.dataset.showroom;
      const sr = GeoPickupPopup.SHOWROOMS[key];
      if (sr) GeoPickupPopup.trackEvent('banner_impression', sr.label);
    }

    dismiss(popup) {
      const key = popup.dataset.showroom;
      const sr = GeoPickupPopup.SHOWROOMS[key];

      popup.classList.add('bos-geo-popup--dismissing');
      document.body.style.overflow = '';

      if (this._trap) { document.removeEventListener('keydown', this._trap, true); this._trap = null; }
      if (this._returnFocus && document.contains(this._returnFocus)) this._returnFocus.focus({ preventScroll: true });
      this._returnFocus = null;

      if (!Shopify.designMode) {
        GeoPickupPopup.setCookie(GeoPickupPopup.COOKIE_NAME, 'dismissed', this.cookieDays);
      }

      if (sr) GeoPickupPopup.trackEvent('banner_dismiss', sr.label);

      setTimeout(() => {
        popup.setAttribute('hidden', '');
        popup.classList.remove('bos-geo-popup--dismissing', 'bos-geo-popup--animate');
      }, 260);
    }

    // ==============================
    // SCROLL + TIME TRIGGERS
    // ==============================
    _setupTriggers(callback) {
      let triggered = false;

      const trigger = () => {
        if (triggered) return;
        triggered = true;
        this._cleanupTriggers();
        callback();
      };

      this._scrollHandler = () => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
        if (docHeight > 0 && (scrollTop / docHeight) * 100 >= this.scrollThreshold) {
          trigger();
        }
      };

      window.addEventListener('scroll', this._scrollHandler, { passive: true });
      this._timeTimer = setTimeout(trigger, this.timeThreshold);
    }

    _cleanupTriggers() {
      if (this._scrollHandler) {
        window.removeEventListener('scroll', this._scrollHandler);
        this._scrollHandler = null;
      }
      if (this._timeTimer) {
        clearTimeout(this._timeTimer);
        this._timeTimer = null;
      }
    }

    // ==============================
    // GEO DETECTION
    // ==============================
    _detectGeo() {
      const cached = GeoPickupPopup.getCookie(GeoPickupPopup.GEO_COOKIE_NAME);

      if (cached) {
        try {
          const geo = JSON.parse(cached);
          const key = this._findShowroomKey(geo.city, geo.region);
          if (key) this._setupTriggers(() => this._showForKey(key));
        } catch (e) {
          // Corrupted cookie — will re-fetch next page load
        }
        return;
      }

      if (!window.fetch) return;

      this._fetchGeo(GeoPickupPopup.GEO_API_URL, (data) => ({
        city: data.city || '',
        region: data.region || ''
      }))
      .then((geo) => this._processGeo(geo.city, geo.region))
      .catch(() => {
        this._fetchGeo(GeoPickupPopup.GEO_API_FALLBACK_URL, (data) => ({
          city: data.city || '',
          region: data.region_name || ''
        }))
        .then((geo) => this._processGeo(geo.city, geo.region))
        .catch(() => {
          // Both APIs failed — no popup
        });
      });
    }

    _fetchGeo(url, parser) {
      return fetch(url)
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then(parser);
    }

    _processGeo(city, region) {
      GeoPickupPopup.setCookie(
        GeoPickupPopup.GEO_COOKIE_NAME,
        JSON.stringify({ city, region }),
        this.cookieDays
      );
      const key = this._findShowroomKey(city, region);
      if (key) this._setupTriggers(() => this._showForKey(key));
    }

    // ==============================
    // SHOWROOM MATCHING
    // ==============================
    _findShowroomKey(city, region) {
      const hostname = window.location.hostname.toLowerCase();
      const cityLower = (city || '').toLowerCase().trim();
      const regionLower = (region || '').toLowerCase().trim();
      let fallbackKey = null;

      for (const [key, sr] of Object.entries(GeoPickupPopup.SHOWROOMS)) {
        if (!sr.domains.includes(hostname)) continue;
        if (sr.cities.includes(cityLower)) return key;
        if (regionLower && sr.regions.some((r) => regionLower.includes(r))) {
          fallbackKey = key;
        }
      }

      return fallbackKey;
    }

    _showForKey(key) {
      const showroom = GeoPickupPopup.SHOWROOMS[key];
      if (!showroom) return;
      if (showroom.pageSlug && this.path.includes(showroom.pageSlug)) return;

      const popup = this.querySelector(`#bos-geo-popup--${key}`);
      if (!popup) return;

      this.open(popup);
    }

    // ==============================
    // COOKIE HELPERS (static)
    // ==============================
    static setCookie(name, value, days) {
      const d = new Date();
      d.setTime(d.getTime() + (days * 24 * 60 * 60 * 1000));
      document.cookie = `${name}=${encodeURIComponent(value)};expires=${d.toUTCString()};path=/;SameSite=Lax`;
    }

    static getCookie(name) {
      const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
      return match ? decodeURIComponent(match[2]) : null;
    }

    // ==============================
    // GA4 TRACKING (static)
    // ==============================
    static trackEvent(eventName, showroomLabel) {
      if (typeof gtag === 'function') {
        gtag('event', eventName, {
          event_category: 'geo_popup',
          event_label: showroomLabel,
          showroom_city: showroomLabel,
          non_interaction: eventName === 'banner_impression'
        });
      }
      if (typeof dataLayer !== 'undefined') {
        dataLayer.push({
          event: eventName,
          event_category: 'geo_popup',
          event_label: showroomLabel,
          showroom_city: showroomLabel
        });
      }
    }
  }

  customElements.define('geo-pickup-popup', GeoPickupPopup);
}